// src/sot/primitives/List.jsx
import React from "react";
import { type as T } from "@/sot/tokens/typography.js";
import Card from "@/components/ui/Card.jsx";

/**
 * List — AfriMed SSOT list container
 * Now supports:
 *  - 24px icons
 *  - controllable padding (default: 12px 16px)
 *  - correct Body Medium support text
 *  - full-bleed OR inset dividers
 */
export default function List({
  items = [],
  className = "",
  paddingX = 4,       // default px-4 → can be changed to 3 later
  paddingY = 4,
  insetDivider = true,
  dividerInsetStart = null,
  dividerInsetEnd = null,
  itemMinHeight = 56,
}) {
  const titleStyle = {
    fontSize: `${T.bodyLarge.size}px`,
    lineHeight: `${T.bodyLarge.line}px`,
    fontWeight: T.bodyLarge.weight,
    letterSpacing: "0.5px",
    color: "var(--Schemes-On-Surface,#040812)",
  };

  const supportStyle = {
    fontSize: `${T.bodyMedium.size}px`,
    lineHeight: `${T.bodyMedium.line}px`,
    fontWeight: T.bodyMedium.weight,
    letterSpacing: "0px",
    color: "var(--Schemes-On-Surface-Variant,#5B6B86)",
  };

  return (
    <Card
      className={[
        "overflow-hidden",
        className,
      ].join(" ")}
    >
      {items.map((it, i) => {
        const {
          leading,
          title,
          support,
          subtitle,
          trailing,
          onClick,
          disabled = false,
          role,
          selected,
          divider = i !== items.length - 1,
          minHeight,
          truncateTitle = true,
          truncateSupport = true,
          titleClassName = "",
          supportClassName = "",
          titleStyleOverride = null,
          supportStyleOverride = null,
        } = it;
        const supportText = support ?? subtitle;

        const resolvedDividerInsetStart =
          dividerInsetStart == null
            ? (
                insetDivider
                  ? `${paddingX * 0.25}rem`
                  : undefined
              )
            : dividerInsetStart;
        const resolvedDividerInsetEnd =
          dividerInsetEnd == null
            ? (
                insetDivider
                  ? "0rem"
                  : undefined
              )
            : dividerInsetEnd;

        return (
          <div key={i}>
            <button
              type="button"
              onClick={onClick}
              disabled={disabled}
              aria-disabled={disabled || undefined}
              role={role}
              aria-selected={selected}
              data-selected={selected || undefined}
              className={`w-full text-left transition bg-transparent data-[selected=true]:bg-[var(--state-primary-12)] ${
                disabled ? "cursor-not-allowed" : "active:scale-[0.998]"
              }`}
            >
              <div
                className="flex items-center gap-3"
                style={{
                  minHeight: `${Number(minHeight || itemMinHeight || 56)}px`,
                  paddingLeft: `${paddingX * 0.25}rem`,
                  paddingRight: `${paddingX * 0.25}rem`,
                  paddingTop: `${paddingY * 0.25}rem`,
                  paddingBottom: `${paddingY * 0.25}rem`,
                }}
              >
                {/* Leading icon (24px) */}
                {typeof leading === "string" ? (
                  <img
                    src={leading}
                    alt=""
                    className="w-6 h-6 object-contain"
                  />
                ) : (
                  leading || null
                )}

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div
                    style={{
                      ...titleStyle,
                      ...(titleStyleOverride || {}),
                    }}
                    className={[
                      truncateTitle ? "truncate" : "",
                      titleClassName,
                    ].join(" ").trim()}
                  >
                    {title}
                  </div>

                  {supportText ? (
                    <div
                      style={{
                        ...supportStyle,
                        ...(supportStyleOverride || {}),
                      }}
                      className={[
                        "mt-[2px]",
                        truncateSupport ? "truncate" : "",
                        supportClassName,
                      ].join(" ")}
                    >
                      {supportText}
                    </div>
                  ) : null}
                </div>

                {/* Trailing element */}
                {typeof trailing === "string" ? (
                  <img
                    src={trailing}
                    alt=""
                    className="w-6 h-6 object-contain opacity-60"
                  />
                ) : (
                  trailing || null
                )}
              </div>
            </button>

            {/* Divider */}
            {divider && (
              <div
              className={`h-px ${
                  insetDivider ? "" : "ml-0 mr-0"
                }`}
                style={{
                  background: "var(--Schemes-Outline-Variant,#E6E7EB)",
                  marginLeft: resolvedDividerInsetStart,
                  marginRight: resolvedDividerInsetEnd,
                }}
              />
            )}
          </div>
        );
      })}
    </Card>
  );
}
